import { Corban } from "./corban.model";
import { CreditPromoter } from "./creditPromoter.model";
import { User } from "./user.model";

export interface FinancialSettings {
  id?: string;
  commissionEmail: string;
  commercialManagerId?: string | null;
  externalCommercialManager?: string | null;
  bankAccount?: string;
  agency?: string;
  bank: string;
  pixKeyType?: FinancialSettingsPixKeyType | string;
  pixKey?: string | null;
  accountDigit?: string;
  sourceIncomeTax?: number | null;
  kaminoId?: string;
  optionBluepay?: boolean;
  commissionModeId?: string | null;
  createdAt?: Date;
  CorbanFinancialSettings?: Corban[];
  CreditPromoterFinancialSettings?: CreditPromoter[];
  status?: FinancialSettingsStatus | null;
  CommercialManager?: User | null;
  taxWithholding?: boolean;
  FinancialSettingsTax?: FinancialSettingsTax[];
  CommissionMode?: CommissionMode | null;
}

export type FinancialSettingsTax = {
  financialSettingsId?: string;
  taxId?: string;
  Tax?: Tax;
};

export type Tax = {
  id?: string;
  name: string;
  percentage: number;
};

export type CommissionMode = {
  id: string;
  repassModel: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

export enum EntityType {
  FINANCIAL_SETTINGS = "FINANCIAL_SETTINGS",
}

export enum AuditOperation {
  CREATE = "CREATE",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
}

export interface AuditLogResponse {
  id: string;
  entityType: EntityType;
  entityId: string;
  changedByUserId?: string;
  changedByUserName?: string;
  changedByUserEmail?: string;
  operation: AuditOperation;
  changes: Array<{
    fieldName: string;
    oldValue: any;
    newValue: any;
    fieldLabel: string;
  }>;
  changeReason?: string;
  totalFieldsChanged: number;
  createdAt: Date;
}

export enum FinancialSettingsPixKeyType {
  DOCUMENT = "DOCUMENT",
  PHONE = "PHONE",
  CPF = "CPF",
  EMAIL = "EMAIL",
  RANDOM = "RANDOM",
  BANK_ACCOUNT = "BANK_ACCOUNT",
}

export const FinancialSettingsPixKeyTypeLabels: Record<
  FinancialSettingsPixKeyType,
  string
> = {
  [FinancialSettingsPixKeyType.DOCUMENT]: "PIX – CNPJ",
  [FinancialSettingsPixKeyType.CPF]: "PIX – CPF",
  [FinancialSettingsPixKeyType.PHONE]: "PIX – Telefone",
  [FinancialSettingsPixKeyType.EMAIL]: "PIX – E-mail",
  [FinancialSettingsPixKeyType.RANDOM]: "PIX – Chave Aleatória (EVP)",
  [FinancialSettingsPixKeyType.BANK_ACCOUNT]: "Banco (Agência/Conta/Dígito)",
};

export const FinancialSettingsPixKeyTypeConfig: Record<
  FinancialSettingsPixKeyType,
  {
    mask: string | null;
    placeholder: string;
    maxLength: number;
  }
> = {
  [FinancialSettingsPixKeyType.DOCUMENT]: {
    mask: "99.999.999/9999-99",
    placeholder: "00.000.000/0000-00",
    maxLength: 18,
  },
  [FinancialSettingsPixKeyType.CPF]: {
    mask: "999.999.999-99",
    placeholder: "000.000.000-00",
    maxLength: 14,
  },
  [FinancialSettingsPixKeyType.EMAIL]: {
    mask: null,
    placeholder: "exemplo@email.com",
    maxLength: 100,
  },
  [FinancialSettingsPixKeyType.PHONE]: {
    mask: "(99) 99999-9999",
    placeholder: "(11) 99999-9999",
    maxLength: 15,
  },
  [FinancialSettingsPixKeyType.RANDOM]: {
    mask: null,
    placeholder: "chave-aleatoria-gerada-pelo-banco",
    maxLength: 200,
  },
  [FinancialSettingsPixKeyType.BANK_ACCOUNT]: {
    mask: null,
    placeholder: "",
    maxLength: 0,
  },
};

// Mapeamento simples usando objetos
export const PixKeyTypeToBackend = {
  [FinancialSettingsPixKeyType.DOCUMENT]: "DOCUMENT",
  [FinancialSettingsPixKeyType.EMAIL]: "EMAIL",
  [FinancialSettingsPixKeyType.PHONE]: "PHONE",
  [FinancialSettingsPixKeyType.CPF]: "DOCUMENT",
  [FinancialSettingsPixKeyType.RANDOM]: "RANDOM",
  [FinancialSettingsPixKeyType.BANK_ACCOUNT]: "BANK_ACCOUNT",
} as const;

export const PixKeyTypeFromBackend = {
  DOCUMENT: FinancialSettingsPixKeyType.DOCUMENT,
  EMAIL: FinancialSettingsPixKeyType.EMAIL,
  PHONE: FinancialSettingsPixKeyType.PHONE,
  RANDOM: FinancialSettingsPixKeyType.RANDOM,
  BANK_ACCOUNT: FinancialSettingsPixKeyType.BANK_ACCOUNT,
} as const;

export type FinancialSettingsStatus = "APPROVED" | "PENDING" | "REJECTED";
